import React, { createContext, useState, useEffect, useContext } from 'react';
import { supabase } from '../supabaseClient';
import { useToast } from '../hooks/use-toast';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    // Auth disabled when Supabase isn't configured — render the app as logged-out
    // instead of crashing to a white screen.
    if (!supabase) { setLoading(false); return; }
    // Check active session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;
      console.log('✅ Profile fetched:', data);
      console.log('📊 Subscription status:', data?.subscription_status);
      setProfile(data);
    } catch (error) {
      console.error('❌ Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email, password, fullName) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
          emailRedirectTo: window.location.origin,
        },
      });

      if (error) throw error;

      // Fire welcome email (non-blocking)
      fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/ai/welcome`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, full_name: fullName }),
      }).catch(() => {});

      // Check if email confirmation is required
      if (data?.user && !data?.session) {
        toast({
          title: 'Check your email!',
          description: 'We sent you a confirmation link. Please check your email to verify your account.',
        });
      } else {
        toast({
          title: 'Success!',
          description: 'Account created successfully!',
        });
      }

      return { data, error: null };
    } catch (error) {
      console.error('Signup error:', error);
      
      // Provide more helpful error messages
      let errorMessage = error.message;
      if (error.message.includes('clone')) {
        errorMessage = 'There was a configuration issue. Please try again or contact support.';
      } else if (error.message.includes('already registered')) {
        errorMessage = 'This email is already registered. Try signing in instead.';
      }
      
      toast({
        title: 'Sign up failed',
        description: errorMessage,
        variant: 'destructive',
      });
      return { data: null, error };
    }
  };

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      toast({
        title: 'Welcome back!',
        description: 'You have successfully signed in.',
      });

      return { data, error: null };
    } catch (error) {
      toast({
        title: 'Sign in failed',
        description: error.message,
        variant: 'destructive',
      });
      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      toast({
        title: 'Signed out',
        description: 'You have been successfully signed out.',
      });
    } catch (error) {
      toast({
        title: 'Sign out failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const isPaid = () => {
    return profile?.subscription_status === 'paid';
  };

  const isAdmin = () => {
    return profile?.is_admin === true;
  };

  const value = {
    user,
    profile,
    loading,
    signUp,
    signIn,
    signOut,
    isPaid,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;